import type { MusicSettings, VoiceoverSettings } from '@/types';

export interface MixInput {
  totalDurationSeconds: number;
  voiceover: VoiceoverSettings;
  voiceoverBlob: Blob | null;
  music: MusicSettings;
  musicBlob: Blob | null;
  sampleRate?: number;
}

/**
 * Renders the final mixed audio track (voiceover + background music, with
 * independent volumes and optional ducking) into a single WAV Blob using an
 * OfflineAudioContext. This same function powers both the live preview audio
 * graph (via a realtime AudioContext elsewhere) and the export pipeline.
 */
export async function renderMixedAudioWav(input: MixInput): Promise<Blob> {
  const sampleRate = input.sampleRate ?? 44100;
  const duration = Math.max(0.1, input.totalDurationSeconds);
  const channels = 2;
  const offlineCtx = new OfflineAudioContext(channels, Math.ceil(duration * sampleRate), sampleRate);

  const masterGain = offlineCtx.createGain();
  masterGain.gain.value = 1;
  masterGain.connect(offlineCtx.destination);

  let voiceoverWindow: { start: number; end: number } | null = null;

  if (input.voiceoverBlob) {
    const buffer = await decodeToBuffer(offlineCtx, input.voiceoverBlob);
    const v = input.voiceover;
    const trimmed = trimBuffer(offlineCtx, buffer, v.trimStart, v.trimEnd);
    const paddedDuration = v.silenceBefore + trimmed.duration + v.silenceAfter;
    const naturalEnd = v.startAt + paddedDuration;
    const targetEnd = v.endAt ?? naturalEnd;
    const audibleStart = v.startAt + v.silenceBefore;

    const source = offlineCtx.createBufferSource();
    source.buffer = trimmed;
    const gain = offlineCtx.createGain();
    gain.gain.value = v.volume;

    // If the requested end is earlier than the natural end, fade out over the
    // last 250ms so the cut isn't abrupt, and cap playback there.
    if (targetEnd < naturalEnd) {
      const fadeStart = Math.max(audibleStart, targetEnd - 0.25);
      gain.gain.setValueAtTime(v.volume, fadeStart);
      gain.gain.linearRampToValueAtTime(0, targetEnd);
    }

    source.connect(gain).connect(masterGain);
    if (audibleStart >= 0 && audibleStart < duration) {
      const playDuration = Math.min(trimmed.duration, Math.max(0, targetEnd - audibleStart));
      source.start(audibleStart, 0, playDuration);
    }
    voiceoverWindow = { start: audibleStart, end: Math.min(targetEnd, duration) };
  }

  if (input.music.enabled && input.musicBlob) {
    const buffer = await decodeToBuffer(offlineCtx, input.musicBlob);
    const m = input.music;
    const trimEnd = m.trimEnd ?? buffer.duration;
    const trimmed = trimBuffer(offlineCtx, buffer, m.trimStart, Math.max(0, buffer.duration - trimEnd));
    const musicGain = offlineCtx.createGain();
    musicGain.gain.value = m.volume;

    scheduleFades(musicGain, m, duration);
    if (m.duckingEnabled && voiceoverWindow) {
      applyDucking(musicGain, m.volume, m.duckingAmount, voiceoverWindow);
    }

    musicGain.connect(masterGain);
    scheduleLoopedSource(offlineCtx, trimmed, musicGain, m.startAt, duration, m.loop);
  }

  const rendered = await offlineCtx.startRendering();
  return audioBufferToWavBlob(rendered);
}

async function decodeToBuffer(ctx: OfflineAudioContext, blob: Blob): Promise<AudioBuffer> {
  const arrayBuffer = await blob.arrayBuffer();
  return ctx.decodeAudioData(arrayBuffer.slice(0));
}

function trimBuffer(ctx: OfflineAudioContext, buffer: AudioBuffer, trimStart: number, trimEnd: number): AudioBuffer {
  const startSample = Math.floor(Math.max(0, trimStart) * buffer.sampleRate);
  const endSample = Math.max(startSample + 1, buffer.length - Math.floor(Math.max(0, trimEnd) * buffer.sampleRate));
  const length = Math.max(1, endSample - startSample);
  const out = ctx.createBuffer(buffer.numberOfChannels, length, buffer.sampleRate);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch).subarray(startSample, startSample + length);
    out.copyToChannel(data, ch);
  }
  return out;
}

function scheduleFades(gainNode: GainNode, music: MusicSettings, totalDuration: number): void {
  const base = music.volume;
  if (music.fadeInSeconds > 0) {
    gainNode.gain.setValueAtTime(0, music.startAt);
    gainNode.gain.linearRampToValueAtTime(base, Math.min(totalDuration, music.startAt + music.fadeInSeconds));
  }
  if (music.fadeOutSeconds > 0) {
    const fadeStart = Math.max(music.startAt, totalDuration - music.fadeOutSeconds);
    gainNode.gain.setValueAtTime(base, fadeStart);
    gainNode.gain.linearRampToValueAtTime(0, totalDuration);
  }
}

function applyDucking(
  gainNode: GainNode,
  baseVolume: number,
  duckingAmount: number,
  window: { start: number; end: number }
): void {
  const duckedVolume = baseVolume * (1 - duckingAmount);
  const rampTime = 0.25;
  gainNode.gain.setValueAtTime(baseVolume, Math.max(0, window.start - rampTime));
  gainNode.gain.linearRampToValueAtTime(duckedVolume, window.start);
  gainNode.gain.setValueAtTime(duckedVolume, Math.max(window.start, window.end - rampTime));
  gainNode.gain.linearRampToValueAtTime(baseVolume, window.end);
}

function scheduleLoopedSource(
  ctx: OfflineAudioContext,
  buffer: AudioBuffer,
  destination: AudioNode,
  startAt: number,
  totalDuration: number,
  loop: boolean
): void {
  if (buffer.duration <= 0) return;
  let cursor = startAt;
  while (cursor < totalDuration) {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(destination);
    const remaining = totalDuration - cursor;
    source.start(cursor, 0, Math.min(buffer.duration, remaining));
    cursor += buffer.duration;
    if (!loop) break;
  }
}

/** Encodes an AudioBuffer to a 16-bit PCM WAV Blob (no external deps needed). */
export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  const channelData: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) channelData.push(buffer.getChannelData(ch));

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channelData[ch][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
}
